'use client';
import { useId, useState, type FormEvent } from 'react';
import { Input } from 'components/UI/Input';
import styles from './bmi.module.scss';

function calculateBmi(heightCm: number, weightKg: number): number {
  const m = heightCm / 100;
  return weightKg > 0 && m > 0 ? weightKg / (m * m) : NaN;
}

export const WidgetToolsCalculatorBmi = () => {
  const heightId = useId();
  const weightId = useId();

  const [height, setHeight] = useState<number | undefined>();
  const [weight, setWeight] = useState<number | undefined>();
  const [result, setResult] = useState<number | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (typeof height === 'number' && typeof weight === 'number' && height > 0 && weight > 0) {
      const bmi = calculateBmi(height, weight);
      if (!Number.isNaN(bmi) && Number.isFinite(bmi)) {
        setResult(bmi);
      }
    }
  }

  return (
    <div className={styles.root}>
      <form onSubmit={onSubmit}>
        <div className={styles.fieldWrapper}>
          <Input
            id={heightId}
            type="number"
            label="Körpergröße"
            suffix="cm"
            value={height ?? ''}
            onChange={(event) => {
              const value = event.currentTarget.valueAsNumber;
              setHeight(Number.isNaN(value) ? undefined : value);
            }}
            min={1}
            max={999}
            required
          />

          <Input
            id={weightId}
            type="number"
            label="Körpergewicht"
            suffix="kg"
            value={weight ?? ''}
            onChange={(event) => {
              const value = event.currentTarget.valueAsNumber;
              setWeight(Number.isNaN(value) ? undefined : value);
            }}
            min={1}
            max={999}
            required
          />
        </div>

        <button className={styles.button} type="submit">
          Auswerten
        </button>
      </form>

      {result !== null && (
        <div className={styles.results} aria-live="polite">
          <div className={styles.headline}>
            Dein Body-Mass-Index beträgt:{' '}
            {new Intl.NumberFormat('de-DE', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            }).format(result)}
          </div>
          <p>So ist das Ergebnis laut Weltgesundheitsorganisation (WHO) einzuordnen:</p>

          <div className={styles.subheadline}>unter 18,5:</div>
          <p>
            Du bist untergewichtig. Lass von einer Ärztin oder einem Arzt abklären, ob gesundheitliche Probleme hinter
            deinem geringen Körpergewicht stecken und besprich mit ihr oder ihm, wie du auf gesunde Weise zunehmen
            kannst.
          </p>

          <div className={styles.subheadline}>18,5 – 24,9:</div>
          <p>Du hast Normalgewicht. Das heißt, Körpergewicht und Körpergröße stehen genau im richtigen Verhältnis.</p>

          <div className={styles.subheadline}>25 – 29,9:</div>
          <p>
            Du hast Übergewicht, auch Präadipositas genannt. Versuche, dich ausgewogener zu ernähren (weniger Fett und
            Kohlenhydrate, mehr Obst und Gemüse) und treibe zwei bis drei Mal pro Woche Sport.
          </p>

          <div className={styles.subheadline}>30 – 39:</div>
          <p>
            Du hast starkes Übergewicht, auch Adipositas genannt (Grad I bei einem Wert zwischen 30 und 34,9, Grad II
            bei einem Wert zwischen 35 und 39,9). Unterschätze das Problem nicht, denn das Übergewicht ist ein
            Risikofaktor für eine Vielzahl von Erkrankungen. Suche am besten eine Ärztin oder einen Arzt auf, um dich
            über eine gesundheitsverträgliche Diät-Form zu informieren.
          </p>

          <div className={styles.subheadline}>40 oder darüber:</div>
          <p>
            Du hast Adipositas Grad III. Dieses Ergebnis sollte eine Warnung für dich sein: Dein Übergewicht ist extrem
            und gesundheitsschädlich. Lass dich medizinisch beraten, wie du unter ärztlicher Kontrolle Gewicht abbauen
            kannst.
          </p>
        </div>
      )}
    </div>
  );
};
